import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import apiClient from './client.js';
import { queryKeys } from '../lib/queryKeys.js';
import { optimisticPacksEnvelope, rollbackPacksEnvelope } from './packs.js';

function serializeFilters (f = {}) {
  const p = {};
  if (f.q) p.q = f.q;
  if (f.managerId) p.managerId = f.managerId;
  if (f.roundStatus) p.roundStatus = f.roundStatus;
  if (f.mode) p.mode = f.mode;
  if (f.ambStatus) p.ambStatus = f.ambStatus;
  return p;
}

/**
 * GET /api/monitoring-overview — pack cards with last-round summary.
 * @param {object} filters — { q?, managerId?, roundStatus?, mode?, ambStatus? }
 */
export async function fetchMonitoringOverview (filters) {
  const { data } = await apiClient.get('/monitoring-overview', { params: serializeFilters(filters) });
  return data;
}

/**
 * GET /api/packs/:packId/rounds — round history for a pack (roundNumber DESC).
 */
export async function fetchPackRounds (packId) {
  const { data } = await apiClient.get(`/packs/${packId}/rounds`);
  return data;
}

/**
 * GET /api/packs/:packId/rounds/:roundId/daily — window matrix for a round.
 * Pending round → 409 round_pending (caller handles).
 */
export async function fetchRoundDaily (packId, roundId) {
  const { data } = await apiClient.get(`/packs/${packId}/rounds/${roundId}/daily`);
  return data;
}

/**
 * GET /api/packs/:packId/monitoring-window?from&to[&step] — ad-hoc window matrix.
 * `matrix` shape is identical to the daily endpoint's. `step='hour'` answers one
 * UTC day (from === to), and the response adds `partial`/`notice` outside the
 * 30-day hourly band.
 */
export async function fetchMonitoringWindow (packId, from, to, step) {
  const params = step && step !== 'day' ? { from, to, step } : { from, to };
  const { data } = await apiClient.get(`/packs/${packId}/monitoring-window`, { params });
  return data;
}

/**
 * GET /api/packs/:packId/monthly?from&to[&sheet=1] — the pack's month series:
 * dense `pack[]` rows with posts/views/median/growth/cpmFact plus a sparse
 * `byAccount[]`. Both bounds are optional: the backend picks the window.
 * `sheet` sends `sheet=1`, which adds the sheet columns.
 */
export async function fetchPackMonthly (packId, from, to, sheet) {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  if (sheet) params.sheet = '1';
  const { data } = await apiClient.get(`/packs/${packId}/monthly`, { params });
  return data;
}

/**
 * GET /api/monitoring2/packs — one collapsed row per pack, paged on the server.
 * Empty values are omitted, so the backend defaults apply.
 * @param {object} filters — { section?, status?, q?, manager?, page?, perPage? }
 */
export async function fetchMonitoring2List (filters = {}, { signal } = {}) {
  const params = {};
  for (const [k, v] of Object.entries(filters)) if (v !== undefined && v !== null && v !== '') params[k] = v;
  const { data } = await apiClient.get('/monitoring2/packs', { params, signal });
  return data;
}

/** GET /api/monitoring2/totals — the list envelope with `totals` { videos, views, packs, asOf, month } (open month) over EVERY pack the list filters match. */
export function useMonitoring2Totals (filters = {}) {
  return useQuery({
    queryKey: ['monitoring2', 'totals', filters],
    queryFn: async ({ signal }) => {
      const params = {};
      for (const [k, v] of Object.entries(filters)) if (v !== undefined && v !== null && v !== '') params[k] = v;
      const { data } = await apiClient.get('/monitoring2/totals', { params, signal });
      return data;
    },
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * PATCH /api/packs/:packId/monitoring-enabled — pack-disable override.
 * @param {boolean} disabled
 */
export async function setPackDisabled (packId, disabled) {
  const { data } = await apiClient.patch(`/packs/${packId}/monitoring-enabled`, { disabled });
  return data;
}

/**
 * POST /api/packs/:packId/rounds — create a round.
 * @param {object} body — { targetPosts: 15|30, startScheduledAt?: ISO, startMode?: 'first_post' }, the only fields sent
 * `startMode: 'first_post'` asks for a date-less round that opens on the first new
 * post; it excludes `startScheduledAt` (422).
 */
export async function createRound (packId, { targetPosts, startScheduledAt, startMode } = {}) {
  const { data } = await apiClient.post(`/packs/${packId}/rounds`, {
    targetPosts,
    ...(startScheduledAt ? { startScheduledAt } : {}),
    ...(startMode ? { startMode } : {}),
  });
  return data;
}

/**
 * DELETE /api/packs/:packId/rounds/:roundId — delete any non-paid round
 * (paid → 409 round_paid_immutable).
 */
export async function deleteRound (packId, roundId) {
  const { data } = await apiClient.delete(`/packs/${packId}/rounds/${roundId}`);
  return data;
}

/**
 * PATCH /api/packs/:packId/rounds/:roundId — edit round dates/target.
 * @param {object} patch — { startedAt?, closedAt?, targetPosts? }
 */
export async function updateRound (packId, roundId, patch) {
  const { data } = await apiClient.patch(`/packs/${packId}/rounds/${roundId}`, patch);
  return data;
}

/**
 * React Query hook — GET /api/monitoring-overview.
 * @param {object} filters — { q?, managerId?, roundStatus?, mode?, ambStatus? }
 */
export function useMonitoringOverview (filters = {}) {
  return useQuery({
    queryKey: queryKeys.rounds.overview(filters),
    queryFn: () => fetchMonitoringOverview(filters),
    keepPreviousData: true,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * React Query hook — GET /api/packs/:packId/rounds.
 */
export function usePackRounds (packId) {
  return useQuery({
    queryKey: queryKeys.rounds.byPack(packId),
    queryFn: () => fetchPackRounds(packId),
    enabled: !!packId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * React Query hook — GET /api/packs/:packId/rounds/:roundId/daily.
 */
export function useRoundDaily (packId, roundId) {
  return useQuery({
    queryKey: queryKeys.rounds.daily(packId, roundId),
    queryFn: () => fetchRoundDaily(packId, roundId),
    enabled: !!packId && !!roundId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * React Query hook — GET /api/packs/:packId/monitoring-window?from&to[&step].
 * The fourth argument carries the step and the caller's `enabled` gate.
 */
export function useMonitoringWindow (packId, from, to, { step = 'day', enabled = true } = {}) {
  const hourly = step === 'hour';
  return useQuery({
    queryKey: queryKeys.rounds.window(packId, hourly ? { from, to, step } : { from, to }),
    queryFn: () => fetchMonitoringWindow(packId, from, to, step),
    enabled: enabled && !!packId && !!from && !!to,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

export function ambPackMonthlyQuery (packId, { from, to, sheet } = {}) {
  return {
    queryKey: queryKeys.rounds.monthly(packId, sheet ? { from, to, sheet: true } : { from: from ?? null, to: to ?? null }),
    queryFn: () => fetchPackMonthly(packId, from, to, sheet),
    enabled: !!packId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  };
}

/**
 * React Query hook — GET /api/packs/:packId/monthly.
 */
export function useAmbPackMonthly (packId, opts = {}) {
  return useQuery(ambPackMonthlyQuery(packId, opts));
}

/**
 * React Query hook — GET /api/monitoring2/packs. The previous page stays on screen
 * while the next one loads. Invalidated as ['monitoring2'] when the card closes.
 */
export function useMonitoring2List (filters = {}) {
  return useQuery({
    queryKey: ['monitoring2', 'list', filters],
    queryFn: ({ signal }) => fetchMonitoring2List(filters, { signal }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * POST /api/packs/:packId/rounds.
 * Usage: mutate({ packId, targetPosts, startScheduledAt, startMode }).
 */
export function useCreateRound () {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ packId, targetPosts, startScheduledAt, startMode } = {}) =>
      createRound(packId, { targetPosts, startScheduledAt, startMode }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.rounds.byPack(variables?.packId) });
      qc.invalidateQueries({ queryKey: queryKeys.rounds.overviewAll });
      qc.invalidateQueries({ queryKey: queryKeys.packs.roundProgress(variables?.packId) });
    },
  });
}

/**
 * DELETE /api/packs/:packId/rounds/:roundId.
 * Usage: mutate({ packId, roundId }).
 */
export function useDeleteRound () {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ packId, roundId } = {}) => deleteRound(packId, roundId),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.rounds.byPack(variables?.packId) });
      qc.invalidateQueries({ queryKey: queryKeys.rounds.overviewAll });
    },
  });
}

/**
 * PATCH /api/packs/:packId/rounds/:roundId.
 * Usage: mutate({ packId, roundId, startedAt?, closedAt?, targetPosts? }).
 */
export function useUpdateRound () {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ packId, roundId, ...patch } = {}) => updateRound(packId, roundId, patch),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.rounds.byPack(variables?.packId) });
      qc.invalidateQueries({ queryKey: queryKeys.packs.roundProgress(variables?.packId) });
      qc.invalidateQueries({ queryKey: queryKeys.rounds.daily(variables?.packId, variables?.roundId) });
      qc.invalidateQueries({ queryKey: queryKeys.rounds.overviewAll });
    },
  });
}

/**
 * PATCH /api/packs/:packId/monitoring-enabled.
 * Usage: mutate({ packId, disabled, leadId? }).
 */
export function useSetPackDisabled () {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ packId, disabled } = {}) => setPackDisabled(packId, disabled),
    onMutate: ({ packId, leadId, disabled } = {}) => optimisticPacksEnvelope(
      qc, leadId, (data) => ({
        ...data,
        packs: (data.packs ?? []).map((p) => (p.id !== packId ? p : { ...p, parsingEnabled: !disabled })),
      }),
    ),
    onError: (_err, _variables, ctx) => rollbackPacksEnvelope(qc, ctx),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.rounds.overviewAll });
      if (variables?.leadId) {
        qc.invalidateQueries({ queryKey: queryKeys.packs.byLead(variables.leadId) });
      }
      qc.invalidateQueries({ queryKey: queryKeys.leads.all });
    },
  });
}

/**
 * POST /api/packs/:packId/rounds/:roundId/close — manual close.
 * Usage: mutate({ packId, roundId, leadId? }).
 */
export function useCloseRound () {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ packId, roundId } = {}) => {
      const { data } = await apiClient.post(`/packs/${packId}/rounds/${roundId}/close`);
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.rounds.byPack(variables?.packId) });
      qc.invalidateQueries({ queryKey: queryKeys.rounds.overviewAll });
      qc.invalidateQueries({ queryKey: queryKeys.packs.roundProgress(variables?.packId) });
      if (variables?.leadId) {
        qc.invalidateQueries({ queryKey: queryKeys.packs.byLead(variables.leadId) });
      }
    },
  });
}
